import { Component, OnInit, OnDestroy, inject, signal, ChangeDetectorRef, EventEmitter, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableDynamicComponent } from '@sharedComponents/table-dynamic/table-dynamic.component';
import { DynamicTableConfig, Column } from '@sharedComponents/table-dynamic/interfaces';
import { ITable } from '@modelos/interfaces/tableHeader.model';
import { Subscription } from 'rxjs';
import { PrimengModule } from '@SharedModules/primeng.module';
import { sharedButtonEmitEvent } from 'app/_sharedFuctions/shared-functions';

/**
 * Dialog that opens TableDynamicComponent (the shared modern table) directly
 * for selecting analytes to copy.
 *
 * KEY DESIGN DECISIONS:
 * 1. localFilter: FALSE — when localFilter=true, _onFilter() in table-dynamic calls
 *    this.dt.filter() which re-emits (onFilter), creating an infinite loop AND
 *    incorrectly filters out all data. Using false routes through (onFilter) output.
 * 2. We intercept (onFilter) output and do client-side filtering ourselves on the
 *    full dataset, without touching table-dynamic internals.
 * 3. Columns are built ONCE and never recreated — same reference prevents table-dynamic's
 *    effect() from resetting column state on every data update.
 */
@Component({
  selector: 'app-assay-analites-copy-dialog',
  standalone: true,
  imports: [CommonModule, TableDynamicComponent, PrimengModule],
  template: `
    <div class="flex flex-column h-full overflow-hidden" style="min-height: 0;">

      <!-- Minimalist Header -->
      <div class="flex-none pb-3 mb-2 border-bottom-1 border-100">
        <h2 class="m-0 text-xl font-bold text-900">Seleccionar Determinaciones</h2>
        <p class="mt-1 mb-0 text-500 text-sm">Seleccione los determinaciones que desea copiar al ensayo actual.</p>
      </div>

      <!-- Modern Shared Table Engine -->
      <div class="flex-grow-1 overflow-hidden" style="min-height: 0;">
        <table-dynamic
          [config]="tableConfigSignal()"
          (onFilter)="handleFilter($event)"
          (onLazyLoad)="handleFilter($event)"
          (onSearch)="handleFilter($event)"
          (onSelectionChangeOutput)="handleSelection($event)">
        </table-dynamic>
      </div>

      <!-- Action Footer -->
      <div class="flex-none flex justify-content-end align-items-center gap-3 pt-4 mt-2 border-top-1 border-200">
        <p-button label="Cancelar" icon="pi pi-times" severity="secondary" [text]="true" (onClick)="close()"></p-button>
        <p-button
          label="Copiar Selección"
          icon="pi pi-copy"
          severity="primary"
          [disabled]="isCopyDisabled()"
          (onClick)="executeCopy()"
          styleClass="px-4">
        </p-button>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; height: 100%; }
    ::ng-deep .p-paginator .p-select,
    ::ng-deep .p-paginator .p-dropdown {
        width: auto !important;
        min-width: 4rem !important;
    }
  `]
})
export class AssayAnalitesCopyDialogComponent implements OnInit, OnDestroy {
  private dialogConfig = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);

  tableConfig: ITable;
  tableConfigSignal = signal<DynamicTableConfig<any> | null>(null);

  /** Full unfiltered data from server */
  private allData: any[] = [];
  /** Stable columns reference — built once, never recreated to avoid table-dynamic effect loops */
  private stableColumns: Column[] | null = null;
  /** Current active filters for client-side filtering */
  private activeFilters: any = {};

  isLoading = false;
  private rowsSelected: any[] = [];
  private subscriptions = new Subscription();
  private buttonEmitEvent = new EventEmitter<{ value: string; parameters: string[]; fn?: Function }>();

  ngOnInit() {
    this.tableConfig = this.dialogConfig.data?.tableConfig;
    if (!this.tableConfig) {
      console.error('[AssayAnalitesCopyDialog] No tableConfig provided');
      return;
    }

    // Link dialog ref so the backend service can close it after copy
    this.tableConfig.dialogRef = this.ref;

    // Track loading
    this.isLoading = this.tableConfig.isLoading.value;
    this.subscriptions.add(
      this.tableConfig.isLoading.subscribe(loading => {
        this.isLoading = loading;
        const current = this.tableConfigSignal();
        if (current) {
          this.tableConfigSignal.set({ ...current, loading });
        }
      })
    );

    // Data subscription
    this.subscriptions.add(
      this.tableConfig.grid.tableData.subscribe(data => {
        if (!data) return;
        this.allData = data;
        this.applyFilters();
      })
    );

    // Button emit wiring
    this.subscriptions.add(sharedButtonEmitEvent(this.buttonEmitEvent, this));

    // Trigger initial data load
    if (this.tableConfig.filters?.filterTable) {
      this.tableConfig.filters.filterTable(0, {});
    }
  }

  /**
   * Applies activeFilters to allData and refreshes the config signal.
   * Columns reference is reused (never recreated) so table-dynamic's effect()
   * sees the same columns and does NOT reset or loop.
   */
  private applyFilters() {
    let filtered = this.allData;

    const filterEntries = Object.entries(this.activeFilters);
    if (filterEntries.length > 0) {
      filtered = this.allData.filter(row =>
        filterEntries.every(([field, filterMeta]: [string, any]) => {
          const value = filterMeta?.value;
          if (value === null || value === undefined || value === '') return true;
          const cellValue = this.getNestedValue(row, field);
          return String(cellValue ?? '').toLowerCase().includes(String(value).toLowerCase());
        })
      );
    }

    this.setConfig(filtered);
  }

  private setConfig(data: any[]) {
    const current = this.tableConfigSignal();

    if (!current || !this.stableColumns) {
      // First build: create stable columns reference once
      this.stableColumns = this.tableConfig.grid.colDef.map(col => ({
        field: col.field,
        header: col.headerName,
        visible: true,
        sortable: true,
        filter: { enable: true, type: 'text' }
      }));

      this.tableConfigSignal.set({
        data,
        totalRecords: data.length,
        first: 0,
        pageSize: 20,
        columns: this.stableColumns,        // ← stable, never replaced
        selectable: true,
        onSelectionChange: { emitFullObject: true },
        showPagination: true,
        localFilter: false,                 // ← FALSE: prevents _onFilter→dt.filter()→loop
        lazy: false,
        hideToolbar: true,
        hideTitle: true,
        showFilters: true,
        hideNewButton: true,
        hideTableButton: true,
        hideColumnSelection: true,
        loading: this.isLoading
      });
    } else {
      // Subsequent: spread to preserve same columns reference — no effect() reset.
      // Use setTimeout to defer the update to the next cycle and avoid NG0103
      // when the update is triggered by a child component emission (like onFilter).
      setTimeout(() => {
        this.tableConfigSignal.set({
          ...current,
          data,
          totalRecords: data.length,
          loading: this.isLoading
        });
      });
    }
  }

  /** Client-side filter handler — called by table-dynamic (onFilter) output */
  handleFilter(event: any) {
    if (!event?.filters) return;
    // Ignore empty global filter object
    const { global, ...columnFilters } = event.filters;
    this.activeFilters = {};

    Object.entries(columnFilters).forEach(([field, filterMeta]: [string, any]) => {
      const value = filterMeta?.value ?? filterMeta?.[0]?.value;
      if (value !== null && value !== undefined && value !== '') {
        this.activeFilters[field] = { value };
      }
    });

    // Global search
    if (global?.value) {
      this.activeFilters['_global'] = { value: global.value };
    }

    this.applyFilters();
  }

  handleSelection(event: any[]) {
    this.rowsSelected = event || [];
    this.tableConfig.grid.rowsSelected = this.rowsSelected;
    if (this.tableConfig.grid.onSelectionChanged) {
      this.tableConfig.grid.onSelectionChanged();
    }
  }

  isCopyDisabled(): boolean {
    const btn = this.tableConfig?.toolbar?.buttons?.[0];
    if (!btn) return true;
    return btn.disabledFn() || this.isLoading;
  }

  executeCopy() {
    const btn = this.tableConfig?.toolbar?.buttons?.[0];
    if (btn?.click?.fn) {
      btn.click.fn();
    } else if (btn?.click?.action) {
      this.buttonEmitEvent.emit({ value: btn.click.action, parameters: btn.click.parameters, fn: null });
    }
  }

  close() {
    this.ref.close();
  }

  private getNestedValue(obj: any, path: string): any {
    if (!obj || !path) return null;
    return path.split('.').reduce((acc, part) => acc?.[part] ?? null, obj);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.stableColumns = null;
    this.allData = [];
    if (this.tableConfig?.grid?.deselectRows) {
      this.tableConfig.grid.deselectRows();
    }
  }
}
